import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';

/**
 * StaffRoute - Wraps the Staff panel
 * - Not Staff: back to their own app
 * - Staff without an MFA-verified (aal2) session: enrol or challenge in place
 * The database is the real boundary: without both, Staff queries return nothing.
 */
export function StaffRoute() {
  const isStaff = useAuthStore((s) => s.profile?.is_admin);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Read from the session's JWT by the MFA API, never from local state.
  const { data: gate, error, refetch } = useQuery({
    queryKey: ['staff-gate'],
    enabled: !!isStaff,
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      const { data: aal, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error) throw error;
      if (aal.currentLevel === 'aal2') return { verified: true, factorId: null };
      const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
      if (factorsError) throw factorsError;
      return { verified: false, factorId: factors.totp[0]?.id ?? null };
    },
  });

  if (!isStaff) return <Navigate to="/overview" replace />;

  return (
    <div className="staff-panel">
      <div className="staff-header">
        <Link to="/staff" className="text-size-medium text-weight-semibold">Staff panel</Link>
        <span>
          <Link to="/staff/activity" className="small-link">Activity</Link>
          {' · '}
          <Link to="/overview" className="small-link">Back to app</Link>
        </span>
      </div>
      {error ? (
        <p className="text-size-small staff-error">{error.message}</p>
      ) : !gate ? (
        <div className="loading-spinner" />
      ) : gate.verified ? (
        <>
          {pathname !== '/staff' && (
            <button
              type="button"
              className="link-style"
              style={{ marginBottom: '1rem' }}
              // Opened from a fresh tab there's no history, so fall back to the search.
              onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/staff'))}
            >
              ← Back
            </button>
          )}
          <Outlet />
        </>
      ) : (
        <VerifyStaff factorId={gate.factorId} onVerified={() => refetch()} />
      )}
    </div>
  );
}

// Enrols a TOTP factor when there's none yet, then asks for a code. A verified code
// raises the session to aal2.
function VerifyStaff({ factorId, onVerified }: { factorId: string | null; onVerified: () => void }) {
  const [enrolment, setEnrolment] = useState<{ id: string; qrCode: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // On a click rather than in an effect, so a re-render can never enrol twice.
  const handleEnrol = async () => {
    setBusy(true);
    setError('');
    // An abandoned enrolment leaves an unverified factor behind; start over.
    const { data: factors } = await supabase.auth.mfa.listFactors();
    for (const f of factors?.all ?? []) {
      if (f.factor_type === 'totp' && f.status === 'unverified') await supabase.auth.mfa.unenroll({ factorId: f.id });
    }
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
    setBusy(false);
    if (error) return setError(error.message);
    setEnrolment({ id: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret });
  };

  const verifyingFactor = factorId ?? enrolment?.id;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verifyingFactor) return;
    setBusy(true);
    setError('');
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: verifyingFactor, code: code.trim() });
    setBusy(false);
    if (error) return setError(error.message);
    onVerified();
  };

  return (
    <div className="staff-verify">
      <h1 className="text-size-large">Verify it's you</h1>
      <p className="text-size-regular text-colour-alt-grey">
        Your session isn't verified with an authenticator app yet, so the Staff panel can't show any Users or
        Workspaces. {factorId ? 'Enter the code from your authenticator app.' : 'Set up an authenticator app: scan the code, then enter the code it shows.'}
      </p>
      {!factorId && !enrolment && (
        <button type="button" className="button is-wide w-button" onClick={handleEnrol} disabled={busy}>
          {busy ? 'Setting up...' : 'Set up authenticator app'}
        </button>
      )}
      {!factorId && enrolment && (
        <>
          <img src={enrolment.qrCode} alt="Authenticator QR code" width={180} height={180} />
          <p className="text-size-tiny text-colour-alt-grey">Or enter this key: <code>{enrolment.secret}</code></p>
        </>
      )}
      {verifyingFactor && (
        <form onSubmit={handleSubmit}>
          <label htmlFor="staff-totp" className="form_label">Authenticator code</label>
          <input
            id="staff-totp"
            className="form_input is-light w-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
          {error && <p className="text-size-small staff-error">{error}</p>}
          <button type="submit" className="button is-wide w-button" disabled={busy}>
            {busy ? 'Verifying...' : 'Verify'}
          </button>
        </form>
      )}
      {!verifyingFactor && error && <p className="text-size-small staff-error">{error}</p>}
    </div>
  );
}
